import React, { useCallback, useMemo, VFC } from 'react';
import classNames from 'classnames';
import {
  STEP_EDITOR_CONTENT_OPTION_INITIAL,
  STEP_EDITOR_CONTENT_OPTION_LIBRARY,
  STEP_EDITOR_CONTENT_OPTION_UPLOAD,
} from 'src/editor/constants';
import { useStepEditorContext } from 'src/store/step-editor';

import { StepEditorContentFormBody, StepEditorContentFormBodyProps } from './form/body';
import { IconButton } from 'src/common/icon-button';
import { LeftStrongIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';
import { StepEditorContentConfigureStageTypeEnum } from './configure/stage-type.enum';
import { BreadCrumbsComponent } from 'src/common/breadcrumbs/component';
import { useEditorNavigator } from 'src/editor/use-editor-navigator.hook';
import { EventBus } from 'src/common/EventBus';
import { useFileUploader } from '../use-file-uploader.hook';
import { useEditorContext } from 'src/store/editor';
import { StepEditorSaveStatusIndicator } from './save-status-indicator';

export interface StepEditorContentPanelProps extends Omit<StepEditorContentFormBodyProps, 'onLoomRecordingComplete'> {
  editMode: boolean;
}

export const StepEditorContentPanel: VFC<StepEditorContentPanelProps> = ({
  activeStage,
  editMode,
  onEmbedUrlChange = functionNoop,
}) => {
  const journey = useEditorContext((state) => state.journey);
  const { contentOption, is_synced, saveStatus, setCurrentConfigurationStage, setContentOption } =
    useStepEditorContext();
  const { navigateToConfigurationStage, navigateToJourneyEdit, navigateToSyncedSteps } = useEditorNavigator();
  const { uploadInProgress } = useFileUploader();

  const isSubPanelActive = useMemo(
    () =>
      ![
        StepEditorContentConfigureStageTypeEnum.CONFIGURE,
        StepEditorContentConfigureStageTypeEnum.NEW,
        StepEditorContentConfigureStageTypeEnum.BOARD,
      ].includes(activeStage),
    [activeStage]
  );

  const onBackButtonClicked = () => {
    if (isSubPanelActive) {
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
      const isNewStepActive = window.location.pathname.endsWith(StepEditorContentConfigureStageTypeEnum.NEW);
      if (!isNewStepActive) {
        if (editMode) {
          navigateToConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE, { replaceHistory: true });
        }
      }
    } else if (activeStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE) {
      EventBus.emit('editor:reset-stage', {
        showInitialStage: false,
        currentConfigurationStage: StepEditorContentConfigureStageTypeEnum.BOARD,
      });
      !journey && is_synced ? navigateToSyncedSteps() : navigateToJourneyEdit();
    } else {
      setContentOption(STEP_EDITOR_CONTENT_OPTION_INITIAL);
      EventBus.emit('editor:reset-stage', {
        showInitialStage: !editMode,
        currentConfigurationStage: StepEditorContentConfigureStageTypeEnum.NEW,
      });
    }
  };

  const onLoomRecordingCompleted = useCallback(
    (_, embedUrl: string) => {
      onEmbedUrlChange(embedUrl);
    },
    [onEmbedUrlChange]
  );

  const breadCrumbItems = useMemo(() => {
    const items = ['step'];
    if (activeStage === StepEditorContentConfigureStageTypeEnum.ANNOTATION) {
      items.push(StepEditorContentConfigureStageTypeEnum.ANNOTATION);
    } else if (activeStage === StepEditorContentConfigureStageTypeEnum.BEHAVIOR) {
      items.push(StepEditorContentConfigureStageTypeEnum.BEHAVIOR);
    }
    return items;
  }, [activeStage]);

  return (
    <div
      className={classNames(
        'absolute left-0 top-0 bottom-0 z-20 transition-all overflow-y-auto overflow-x-hidden bg-bedrock-white space-y-lg pt-lg px-md h-screen',
        {
          'pb-xl': contentOption !== STEP_EDITOR_CONTENT_OPTION_UPLOAD,
          'w-[656px] border-r-2 border-bedrock-light-gray': contentOption === STEP_EDITOR_CONTENT_OPTION_LIBRARY,
          'w-[368px]': contentOption !== STEP_EDITOR_CONTENT_OPTION_LIBRARY,
        }
      )}
    >
      <div className='flex justify-between items-center'>
        <div className='flex items-center space-x-sm'>
          <IconButton disabled={uploadInProgress} onClick={onBackButtonClicked}>
            <LeftStrongIcon />
          </IconButton>
          {isSubPanelActive && (
            <BreadCrumbsComponent
              value={activeStage}
              items={breadCrumbItems}
              itemOptions={breadCrumbItems.map((b) => ({ case: 'capitalize' }))}
              onItemClick={onBackButtonClicked}
            />
          )}
        </div>
        <div className='flex gap-2 justify-end'>
          <StepEditorSaveStatusIndicator saveStatus={saveStatus} />
        </div>
      </div>
      <StepEditorContentFormBody
        activeStage={activeStage}
        onLoomRecordingComplete={onLoomRecordingCompleted}
        onEmbedUrlChange={onEmbedUrlChange}
      />
    </div>
  );
};
